/**
 * Hour prices of the object's tariff (`GET /sites/:id/tariff`):
 *   - DAM objects (`tariff_source = dam`): with `date` — the day's DAM prices
 *     with the object's correction, ₴/kWh; without — null;
 *   - `tariff_source = template`: the 24-cell rates (zone tariff).
 * `PUT` stores the 24 wall-clock cells of a zone tariff; the object
 * (`SiteResponse.zone_rates_per_kwh`) answers them back for any tariff source.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TariffProfileResponse } from 'shared';

import { api } from '../lib/api-client.js';

const url = (siteId: string) => `/api/sites/${encodeURIComponent(siteId)}/tariff`;

export function useTariff(siteId: string, date: string | null, enabled = true) {
  return useQuery({
    queryKey: ['tariff', siteId, date],
    queryFn: () =>
      api
        .get<{ tariff: TariffProfileResponse | null }>(
          date ? `${url(siteId)}?date=${encodeURIComponent(date)}` : url(siteId),
        )
        .then((r) => r.tariff),
    enabled: enabled && !!siteId,
    staleTime: 10 * 60_000,
  });
}

export function useSaveTariff(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cells: number[]) =>
      api.put<{ tariff: TariffProfileResponse }>(url(siteId), { hourly_local_per_kwh: cells }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tariff', siteId] });
      void qc.invalidateQueries({ queryKey: ['site', siteId] });
      // «Гроші» are counted at these rates.
      void qc.invalidateQueries({ queryKey: ['site-money', siteId] });
    },
  });
}
